import { FunctionComponent, useState, useEffect, useCallback } from 'react';
import {
    Box, Button, Header, Table, SpaceBetween, Modal,
    Select, SelectProps, DateRangePicker, DateRangePickerProps,
    PropertyFilter, PropertyFilterProps,
    Pagination, CollectionPreferences,
} from '@amzn/awsui-components-react';
import { Order } from '@amzn/ito-client';
import { useFlashBarItemsActions } from 'common/UseFlashBarItems/useFlashBarItems';
import { AddBulkOrdersModal, AddBulkOrdersResult } from './AddBulkOrdersModal';
import { CompleteBulkOrderModal } from './CompleteBulkOrderModal';
import { getString, getPropertyFilterString } from 'common/ui-string-labels/ui-strings-utils';
import { buildDateRangeFilter, validateDateRange, DATE_RANGE_RELATIVE_OPTIONS, formatRelativeRange } from 'common/utils/date-range-filter-utils';
import { VENDOR_FILTER_OPTIONS, STATUS_FILTER_OPTIONS } from './interfaces';
import { BulkOrderStatus } from 'configuration/config';
import { buildColumnDefinitions, buildFilteringProperties, buildVisibleContentOptions, defaultPagingOptions } from 'common/utils/table-utils';
import { convertTokensToPropertyFilter } from 'common/utils/property-filter-helper';
import { describeError } from 'common/utils/error-utils';
import { ColumnSetting } from 'common/utils/config-models';
import { usePreferences } from 'hooks/usePreferences';
import { BulkOrdersService, BulkOrderPartialError } from 'services/bulk-orders-service';
import tableConfig from './bulk-orders.table-config.json';

const PREFERENCES_KEY = 'SubmittedBulkOrdersPreferences';
const columns: ColumnSetting[] = tableConfig.columns as ColumnSetting[];
const VISIBLE_CONTENT_COLUMN_OPTIONS = buildVisibleContentOptions(columns);
const FILTERING_PROPERTIES = buildFilteringProperties(columns);

export interface BaseListParams {
    vendor?: string;
    status?: string;
    dateFilter?: [string, string];
}

/** Builds the vendor/status/date portion of listBulkOrders params shared by both fetch paths. */
export function buildBaseListParams(
    vendorFilter: SelectProps.Option,
    statusFilter: SelectProps.Option,
    dateRange: DateRangePickerProps.Value | null,
): BaseListParams {
    const params: BaseListParams = {};
    if (vendorFilter.value && vendorFilter.value !== 'all') params.vendor = vendorFilter.value;
    if (statusFilter.value && statusFilter.value !== 'all') params.status = statusFilter.value;
    if (dateRange) {
        const filter = buildDateRangeFilter(dateRange);
        if (filter) params.dateFilter = filter;
    }
    return params;
}

export interface BulkOrdersTableProps {
    useFlashBarItemsActions: useFlashBarItemsActions;
}

export const BulkOrdersTable: FunctionComponent<BulkOrdersTableProps> = ({ useFlashBarItemsActions }) => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedItems, setSelectedItems] = useState<Order[]>([]);
    const [submitModalVisible, setSubmitModalVisible] = useState(false);
    const [completeModalVisible, setCompleteModalVisible] = useState(false);

    // Filters
    const [vendorFilter, setVendorFilter] = useState<SelectProps.Option>(VENDOR_FILTER_OPTIONS[0]);
    const [statusFilter, setStatusFilter] = useState<SelectProps.Option>(STATUS_FILTER_OPTIONS[0]);
    const [dateRange, setDateRange] = useState<DateRangePickerProps.Value | null>(null);
    const [filterQuery, setFilterQuery] = useState<PropertyFilterProps.Query>({ tokens: [], operation: 'and' });

    // Preferences (persisted to localStorage)
    const { currentPreferences, onPreferencesConfirmHandler } = usePreferences({
        preferencesKey: PREFERENCES_KEY,
        refreshData: () => {},
        columnsSettings: columns,
        defaultPaging: defaultPagingOptions[0].value,
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [totalResults, setTotalResults] = useState(0);

    /** Fetches orders from the backend, matching ManageOrders' pattern: PropertyFilter
     * tokens are converted directly to a filters map (via convertTokensToPropertyFilter)
     * and sent in a single request. */
    const fetchOrders = useCallback(async (page: number) => {
        setLoading(true);
        try {
            const keyedFilters = convertTokensToPropertyFilter(filterQuery.tokens);
            const baseParams = buildBaseListParams(vendorFilter, statusFilter, dateRange);
            const pageSize = currentPreferences.pageSize!;

            const result = await BulkOrdersService.instance.listBulkOrders({
                ...baseParams,
                pageSize,
                pageIndex: page,
                filters: Object.keys(keyedFilters).length > 0 ? keyedFilters : undefined,
            });

            setOrders(result.orders);
            setTotalResults(result.total);
        } catch (error) {
            useFlashBarItemsActions.addErrorFlashBarItem(describeError(error, 'bulkOrders.flashbar.error'));
        } finally {
            setLoading(false);
        }
    }, [vendorFilter, statusFilter, dateRange, filterQuery, currentPreferences.pageSize]);

    useEffect(() => {
        setCurrentPage(1);
        fetchOrders(1);
    }, [fetchOrders]);

    // Add Bulk Orders modal state
    const onSubmitBulkOrders = async (result: AddBulkOrdersResult) => {
        setSubmitModalVisible(false);
        useFlashBarItemsActions.addLoadingFlashBarItem?.(getString('bulkOrders.submittedOrders.submitting', { count: result.templates.length }));
        try {
            await BulkOrdersService.instance.submitBulkOrders(result);
            useFlashBarItemsActions.clearFlashBarItems?.();
            useFlashBarItemsActions.addSuccessFlashBarItem(getString('bulkOrders.submittedOrders.submitSuccess', { count: result.templates.length }));
            await fetchOrders(1);
        } catch (error) {
            useFlashBarItemsActions.clearFlashBarItems?.();
            if (error instanceof BulkOrderPartialError && error.createdOrders.length > 0) {
                useFlashBarItemsActions.addErrorFlashBarItem(
                    getString('bulkOrders.flashbar.error') + ` (${error.createdOrders.length}/${result.templates.length} succeeded)`
                );
            } else {
                useFlashBarItemsActions.addErrorFlashBarItem(describeError(error, 'bulkOrders.flashbar.error'));
            }
            await fetchOrders(1);
        }
    };

    const canComplete = selectedItems.length > 0 && selectedItems.every(o => o.status === BulkOrderStatus.PENDING_RESELLER);
    const canCancel = selectedItems.length > 0 && selectedItems.every(o => o.status === BulkOrderStatus.NEW || o.status === BulkOrderStatus.PENDING_RESELLER);

    const confirmComplete = async () => {
        setCompleteModalVisible(false);
        const orderIds = selectedItems.map(o => o.orderId!);
        useFlashBarItemsActions.addLoadingFlashBarItem?.(getString('bulkOrders.submittedOrders.completing', { count: orderIds.length }));
        try {
            await BulkOrdersService.instance.updateOrdersStatus(orderIds, BulkOrderStatus.COMPLETED);
            useFlashBarItemsActions.clearFlashBarItems?.();
            useFlashBarItemsActions.addSuccessFlashBarItem(getString('bulkOrders.submittedOrders.completeSuccess', { count: orderIds.length }));
            setSelectedItems([]);
            await fetchOrders(1);
        } catch (error) {
            useFlashBarItemsActions.clearFlashBarItems?.();
            useFlashBarItemsActions.addErrorFlashBarItem(describeError(error, 'bulkOrders.flashbar.error'));
        }
    };

    const [cancelModalVisible, setCancelModalVisible] = useState(false);

    const onCancel = async () => {
        setCancelModalVisible(false);
        useFlashBarItemsActions.addLoadingFlashBarItem?.(getString('bulkOrders.submittedOrders.cancelling', { count: selectedItems.length }));
        const results = await Promise.allSettled(
            selectedItems.map(item =>
                BulkOrdersService.instance.updateOrderStatus(item.orderId!, BulkOrderStatus.CANCELLED)
            )
        );
        const failedCount = results.filter(r => r.status === 'rejected').length;
        useFlashBarItemsActions.clearFlashBarItems?.();
        if (failedCount === 0) {
            useFlashBarItemsActions.addSuccessFlashBarItem(
                getString('bulkOrders.submittedOrders.cancelSuccess', { count: selectedItems.length })
            );
        } else {
            const firstRejection = results.find(r => r.status === 'rejected') as PromiseRejectedResult | undefined;
            useFlashBarItemsActions.addErrorFlashBarItem(
                describeError(firstRejection?.reason, 'bulkOrders.flashbar.error')
            );
        }
        setSelectedItems([]);
        await fetchOrders(1);
    };

    return (
        <div style={{ padding: '0 24px' }}>
            <Table<Order>
                data-testid="bulk-orders-table"
                variant="full-page"
                selectionType="multi"
                resizableColumns
                wrapLines
                selectedItems={selectedItems}
                onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
                header={
                    <Header
                        variant="h1"
                        counter={`(${totalResults})`}
                        description={getString('bulkOrders.submittedOrders.description')}
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button data-testid="bulk-orders-complete-button" onClick={() => setCompleteModalVisible(true)} disabled={!canComplete}>{getString('bulkOrders.submittedOrders.completeOrder')}</Button>
                                <Button data-testid="bulk-orders-cancel-button" onClick={() => setCancelModalVisible(true)} disabled={!canCancel}>{getString('bulkOrders.submittedOrders.cancelOrder')}</Button>
                                <Button data-testid="bulk-orders-add-button" variant="primary" onClick={() => setSubmitModalVisible(true)}>{getString('bulkOrders.submittedOrders.addBulkOrders')}</Button>
                            </SpaceBetween>
                        }
                    >
                        {getString('bulkOrders.submittedOrders.title')}
                    </Header>
                }
                filter={
                    <SpaceBetween direction="horizontal" size="s">
                        <div data-testid="bulk-orders-property-filter" style={{ width: "500px" }}>
                            <PropertyFilter
                                query={filterQuery}
                                onChange={({ detail }) => setFilterQuery(detail)}
                                filteringProperties={FILTERING_PROPERTIES}
                                filteringPlaceholder={getString('bulkOrders.table.filterPlaceholder')}
                                i18nStrings={getPropertyFilterString()}
                                expandToViewport
                            />
                        </div>
                        <Select
                            selectedOption={vendorFilter}
                            onChange={({ detail }) => setVendorFilter(detail.selectedOption)}
                            options={VENDOR_FILTER_OPTIONS}
                            selectedAriaLabel="Selected"
                        />
                        <Select
                            selectedOption={statusFilter}
                            onChange={({ detail }) => setStatusFilter(detail.selectedOption)}
                            options={STATUS_FILTER_OPTIONS}
                            selectedAriaLabel="Selected"
                        />
                        <DateRangePicker
                            value={dateRange}
                            onChange={({ detail }) => setDateRange(detail.value)}
                            placeholder={getString('bulkOrders.filters.dateRangePlaceholder')}
                            relativeOptions={DATE_RANGE_RELATIVE_OPTIONS}
                            isValidRange={validateDateRange}
                            i18nStrings={{
                                relativeModeTitle: getString('manageOrders.dateRangePicker.relativeModeTitle'),
                                absoluteModeTitle: getString('manageOrders.dateRangePicker.absoluteModeTitle'),
                                relativeRangeSelectionHeading: getString('manageOrders.dateRangePicker.relativeRangeSelectionHeading'),
                                cancelButtonLabel: getString('manageOrders.dateRangePicker.cancelButtonLabel'),
                                applyButtonLabel: getString('manageOrders.dateRangePicker.applyButtonLabel'),
                                clearButtonLabel: getString('manageOrders.dateRangePicker.clearButtonLabel'),
                                customRelativeRangeOptionLabel: getString('manageOrders.dateRangePicker.customRelativeRangeOptionLabel'),
                                customRelativeRangeOptionDescription: getString('manageOrders.dateRangePicker.customRelativeRangeOptionDescription'),
                                customRelativeRangeDurationLabel: getString('manageOrders.dateRangePicker.customRelativeRangeDurationLabel'),
                                customRelativeRangeDurationPlaceholder: getString('manageOrders.dateRangePicker.customRelativeRangeDurationPlaceholder'),
                                customRelativeRangeUnitLabel: getString('manageOrders.dateRangePicker.customRelativeRangeUnitLabel'),
                                formatRelativeRange,
                            }}
                            dateOnly
                        />
                    </SpaceBetween>
                }
                columnDefinitions={buildColumnDefinitions(tableConfig.columns as ColumnSetting[], currentPreferences.visibleContent)}
                items={orders}
                loading={loading}
                loadingText={getString('bulkOrders.table.loadingText')}
                stripedRows={currentPreferences.stripedRows}
                pagination={
                    <Pagination
                        currentPageIndex={currentPage}
                        pagesCount={Math.ceil(totalResults / currentPreferences.pageSize!) || 1}
                        onChange={({ detail }) => {
                            setCurrentPage(detail.currentPageIndex);
                            fetchOrders(detail.currentPageIndex);
                        }}
                    />
                }
                preferences={
                    <CollectionPreferences
                        title={getString('common.preferences.title')}
                        confirmLabel={getString('common.preferences.confirmLabel')}
                        cancelLabel={getString('common.preferences.cancelLabel')}
                        preferences={currentPreferences}
                        onConfirm={onPreferencesConfirmHandler}
                        pageSizePreference={{ title: getString('common.preferences.pageSize.title'), options: defaultPagingOptions }}
                        stripedRowsPreference={{ label: getString('common.preferences.stripedRows.label'), description: getString('common.preferences.stripedRows.description') }}
                        visibleContentPreference={{ title: getString('common.preferences.visibleContentPreference.title'), options: [{ label: getString('common.preferences.visibleContentPreference.columns'), options: VISIBLE_CONTENT_COLUMN_OPTIONS }] }}
                    />
                }
                empty={<Box textAlign="center"><b>{getString('bulkOrders.table.emptyTitle')}</b></Box>}
            />

            <CompleteBulkOrderModal
                visible={completeModalVisible}
                orders={selectedItems}
                onDismiss={() => setCompleteModalVisible(false)}
                onConfirm={confirmComplete}
            />

            <AddBulkOrdersModal
                visible={submitModalVisible}
                onDismiss={() => setSubmitModalVisible(false)}
                onSubmit={onSubmitBulkOrders}
            />

            <Modal
                data-testid="cancel-bulk-order-modal"
                visible={cancelModalVisible}
                onDismiss={() => setCancelModalVisible(false)}
                header={getString('bulkOrders.submittedOrders.cancelOrder')}
                footer={
                    <Box float="right"><SpaceBetween direction="horizontal" size="xs">
                        <Button data-testid="cancel-bulk-order-back" variant="link" onClick={() => setCancelModalVisible(false)}>{getString('common.preferences.cancelLabel')}</Button>
                        <Button data-testid="cancel-bulk-order-confirm" variant="primary" onClick={onCancel}>{getString('bulkOrders.submittedOrders.cancelOrder')}</Button>
                    </SpaceBetween></Box>
                }
            >
                {getString('bulkOrders.submittedOrders.cancelConfirmMessage', { count: selectedItems.length })}
            </Modal>
        </div>
    );
};